/**
 * §pilot-review — client context for "Report a problem".
 *
 * Two tiny ring buffers, installed once at module import (AppShell imports
 * this), so a report can carry what actually happened on the client:
 *   • the last few window errors / unhandled rejections (message only —
 *     no stack upload, no screenshots: privacy + size are the design rule);
 *   • the last few conversation ids the operator LOOKED AT (pages that
 *     render a conversation call noteConversationRef) — the cheap client-side
 *     answer to "which conversation were you on when it broke".
 */

const MAX_ERRORS = 10;
const MAX_REFS = 3;

const errorRing: string[] = [];
const convRing: string[] = [];

let installed = false;

function pushError(message: string): void {
  const msg = (message || "").trim().slice(0, 300);
  if (!msg) return;
  errorRing.push(msg);
  if (errorRing.length > MAX_ERRORS) errorRing.shift();
}

/** Idempotent: safe to call from every AppShell mount. */
export function installProblemReportBuffers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e: ErrorEvent) => {
    pushError(e.message || String(e.error ?? ""));
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r = e.reason;
    pushError(r instanceof Error ? r.message : String(r ?? ""));
  });
}

/** Called by conversation views so a report knows what was on screen. */
export function noteConversationRef(ref: string): void {
  const r = (ref || "").trim();
  if (!r) return;
  const i = convRing.indexOf(r);
  if (i !== -1) convRing.splice(i, 1);
  convRing.push(r);
  if (convRing.length > MAX_REFS) convRing.shift();
}

export function recentConsoleErrors(): string[] {
  return [...errorRing];
}

export function recentConversationRefs(): string[] {
  // Newest first — the server keeps at most 3 anyway.
  return [...convRing].reverse();
}
